import React, { useEffect, useState } from "react";
import axios from "axios";
import itemImg from "../../../../Assets/items.png";
import "./Items.css";

const FIVE_MINUTES = 5 * 60 * 1000;
const baseURL = "https://fliplyn-b-c.fly.dev";
const ALL_ITEMS_ID = "ALL_ITEMS";

/* ---------------- VEG / NON-VEG ICONS ---------------- */
const VegIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <rect
      x="1"
      y="1"
      width="16"
      height="16"
      fill="white"
      stroke="green"
      strokeWidth="2"
    />
    <circle cx="9" cy="9" r="5" fill="green" />
  </svg>
);

const NonVegIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <rect
      x="1"
      y="1"
      width="16"
      height="16"
      fill="white"
      stroke="red"
      strokeWidth="2"
    />
    <circle cx="9" cy="9" r="5" fill="red" />
  </svg>
);

export default function Items({
  cart = {},
  onAdd,
  onRemove,
  categoryId,
  stallId,
  onItemsLoaded,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL");

  /* ---------------- MOUNT LOG (RUNS ONCE) ---------------- */
  useEffect(() => {
    console.log("🟢 Items.jsx mounted");
  }, []);

  /* ---------------- FETCH ITEMS ---------------- */
  useEffect(() => {
    console.log("📂 categoryId:", categoryId);
    console.log("🏪 stallId:", stallId);

    if (!categoryId) {
      console.warn("⚠️ No categoryId, clearing items");
      setItems([]);
      setLoading(false);
      return;
    }

    let CACHE_KEY = `items_${categoryId}`;

    const saveToCache = (key, data) => {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ data, timestamp: Date.now() })
        );
        console.log(`💾 Cache saved: ${key}`, data.length);
      } catch (err) {
        console.error("❌ Cache save failed", err);
      }
    };

    const fetchCategoryItems = async () => {
      console.log("🌐 Fetching CATEGORY items:", categoryId);
      try {
        const res = await axios.get(
          `${baseURL}/items/items/category/${categoryId}`
        );

        const arr = Array.isArray(res.data) ? res.data : [];
        setItems(arr);
        onItemsLoaded?.(arr);
        saveToCache(CACHE_KEY, arr);
      } catch (err) {
        console.error("❌ Category fetch failed", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchAllItems = async () => {
      CACHE_KEY = "items_ALL_ITEMS";
      console.log("🌐 Fetching ALL items for stall:", stallId);

      try {
        const res = await axios.get(
          `${baseURL}/items/stall/${stallId}`
        );

        const arr = Array.isArray(res.data) ? res.data : [];
        setItems(arr);
        onItemsLoaded?.(arr);
        saveToCache(CACHE_KEY, arr);

        // 🔥 Critical for cart summary
        localStorage.setItem("menuItemsList", JSON.stringify(arr));
        console.log("🧠 menuItemsList saved:", arr.length);
      } catch (err) {
        console.error("❌ ALL items fetch failed", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    /* ---------- CACHE CHECK ---------- */
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const fresh = Date.now() - parsed.timestamp < FIVE_MINUTES;

        if (fresh && Array.isArray(parsed.data)) {
          console.log(`⚡ Using cache: ${CACHE_KEY}`);
          setItems(parsed.data);
          onItemsLoaded?.(parsed.data);
          setLoading(false);

          if (categoryId === ALL_ITEMS_ID) {
            localStorage.setItem(
              "menuItemsList",
              JSON.stringify(parsed.data)
            );
          }
          return;
        }
      }
    } catch (err) {
      console.error("❌ Cache read failed", err);
    }

    if (categoryId === ALL_ITEMS_ID) {
      fetchAllItems();
    } else {
      fetchCategoryItems();
    }
  }, [categoryId, stallId, onItemsLoaded]);

  /* ---------------- QTY ---------------- */
  const getQty = (id) => cart[id] ?? cart[String(id)] ?? 0;

  /* ---------------- FILTER ---------------- */
  const filteredItems = items.filter((item) => {
    if (filterType === "ALL") return true;
    if (filterType === "VEG") return item.is_veg === true;
    if (filterType === "NONVEG") return item.is_veg === false;
    return true;
  });

  if (loading) {
    return <div className="itemsWrapper">Loading...</div>;
  }

  return (
    <div className="itemsWrapper">
      {/* FILTER */}
      <div className="filterRow">
        <button
          className={`filterBtn ${filterType === "ALL" ? "active" : ""}`}
          onClick={() => setFilterType("ALL")}
        >
          All
        </button>

        <button
          className={`filterBtn ${filterType === "VEG" ? "active" : ""}`}
          onClick={() => setFilterType("VEG")}
        >
          <VegIcon /> Veg
        </button>

        <button
          className={`filterBtn ${filterType === "NONVEG" ? "active" : ""}`}
          onClick={() => setFilterType("NONVEG")}
        >
          <NonVegIcon /> Non-Veg
        </button>
      </div>

      {/* ITEMS */}
      <div className="itemsGrid">
        {filteredItems.map((item) => {
          const qty = getQty(item.id);

          return (
            <div key={item.id} className="itemBox">
              <div className="vegIndicator">
                {item.is_veg ? <VegIcon /> : <NonVegIcon />}
              </div>

              <img
                src={item.image_url || itemImg}
                alt={item.name}
                className="itemImage"
              />

              <div className="itemInfo">
                <h3>{item.name}</h3>

                <div className="itemBottom">
                  <span className="itemPrice">₹ {item.price}</span>

                  {qty === 0 ? (
                    <button
                      className="addButton"
                      onClick={() => onAdd(item)}
                    >
                      + Add
                    </button>
                  ) : (
                    <div className="quantityBox">
                      <button
                        className="quantityBtn"
                        onClick={() => onRemove(item)}
                      >
                        −
                      </button>
                      <span>{qty}</span>
                      <button
                        className="quantityBtn"
                        onClick={() => onAdd(item)}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
